import React, { useState, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import {
  createKanbanBoard,
  updateKanbanBoard,
  updateConsultantBoardAssignments,
} from "../../../services/crmApi";
import {
  fetchSalesPersonsNormalized,
  updateSalesPersonBoards,
} from "../../../services/salesPersonApi";
import { FiX, FiUser, FiCheck } from "react-icons/fi";

const BoardManagementModal = ({ board, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sales person assignment
  const [salesPersons, setSalesPersons] = useState([]);
  const [loadingSps, setLoadingSps] = useState(false);
  const [selectedSpIds, setSelectedSpIds] = useState([]);
  const [initialSpIds, setInitialSpIds] = useState([]);

  // Consultants with CRM access (type === "Consultant" from the combined SP endpoint)
  const [consultantsWithRole, setConsultantsWithRole] = useState([]);
  const [selectedConsultantIds, setSelectedConsultantIds] = useState([]);
  const [initialConsultantIds, setInitialConsultantIds] = useState([]);

  const [spDropdownOpen, setSpDropdownOpen] = useState(false);
  const spDropdownRef = useRef(null);

  // Close the sales-person dropdown when the user clicks outside of it.
  useEffect(() => {
    if (!spDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (
        spDropdownRef.current &&
        !spDropdownRef.current.contains(e.target)
      ) {
        setSpDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [spDropdownOpen]);

  // Single fetch: the sales-persons endpoint now returns both SalesPerson and
  // Consultant (hasSalesPersonRole) records. Split by the type field.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingSps(true);
        const list = await fetchSalesPersonsNormalized();
        if (cancelled) return;
        setSalesPersons(
          list
            .filter((item) => item.type === "SalesPerson")
            .map((item) => ({
              ...item,
              assignedBoardIds: Array.isArray(item.assignedBoardIds) ? item.assignedBoardIds : [],
            })),
        );
        setConsultantsWithRole(
          list
            .filter((item) => item.type === "Consultant")
            .map((item) => ({
              ...item,
              assignedBoardIds: Array.isArray(item.assignedBoardIds) ? item.assignedBoardIds : [],
            })),
        );
      } catch (_) {
        if (!cancelled) {
          setSalesPersons([]);
          setConsultantsWithRole([]);
        }
      } finally {
        if (!cancelled) setLoadingSps(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (board) {
      setFormData({
        name: board.name || "",
        description: board.description || "",
      });
    }
  }, [board]);

  // When the board id and SP list are both available, derive which SPs already
  // have this board assigned so we can preselect them in the dropdown.
  useEffect(() => {
    if (!board?._id || salesPersons.length === 0) {
      setInitialSpIds([]);
      setSelectedSpIds([]);
      return;
    }
    const associated = salesPersons
      .filter((sp) =>
        (sp.assignedBoardIds || [])
          .map((b) => (typeof b === "string" ? b : b?._id || b?.id))
          .includes(board._id),
      )
      .map((sp) => sp._id);
    setInitialSpIds(associated);
    setSelectedSpIds(associated);
  }, [board?._id, salesPersons]);

  // Derive which consultants already have this board in their assignedBoardIds.
  useEffect(() => {
    if (!board?._id || consultantsWithRole.length === 0) {
      setInitialConsultantIds([]);
      setSelectedConsultantIds([]);
      return;
    }
    const bid = String(board._id);
    const associated = consultantsWithRole
      .filter((c) =>
        (c.assignedBoardIds || [])
          .map((b) => (typeof b === "object" ? String(b?._id || b?.id || "") : String(b)))
          .includes(bid),
      )
      .map((c) => c._id);
    setInitialConsultantIds(associated);
    setSelectedConsultantIds(associated);
  }, [board?._id, consultantsWithRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const toggleSpSelection = (spId) => {
    setSelectedSpIds((prev) =>
      prev.includes(spId) ? prev.filter((id) => id !== spId) : [...prev, spId],
    );
  };

  const toggleConsultantSelection = (cId) => {
    setSelectedConsultantIds((prev) =>
      prev.includes(cId) ? prev.filter((id) => id !== cId) : [...prev, cId],
    );
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Board name is required");
      return false;
    }
    if (formData.name.trim().length < 3) {
      setError("Board name must be at least 3 characters");
      return false;
    }
    return true;
  };

  const syncConsultantAssignments = async (boardId) => {
    if (!boardId) return;
    const toAdd = selectedConsultantIds.filter((id) => !initialConsultantIds.includes(id));
    const toRemove = initialConsultantIds.filter((id) => !selectedConsultantIds.includes(id));
    if (toAdd.length === 0 && toRemove.length === 0) return;

    const ops = [];
    for (const cId of toAdd) {
      const c = consultantsWithRole.find((c) => c._id === cId);
      const existing = (c?.assignedBoardIds || [])
        .map((b) => (typeof b === "object" ? String(b?._id || b?.id || "") : String(b)))
        .filter(Boolean);
      ops.push(updateConsultantBoardAssignments(cId, Array.from(new Set([...existing, boardId]))));
    }
    for (const cId of toRemove) {
      const c = consultantsWithRole.find((c) => c._id === cId);
      const existing = (c?.assignedBoardIds || [])
        .map((b) => (typeof b === "object" ? String(b?._id || b?.id || "") : String(b)))
        .filter(Boolean);
      ops.push(updateConsultantBoardAssignments(cId, existing.filter((id) => id !== boardId)));
    }

    const results = await Promise.allSettled(ops);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      toast.error(
        `Board saved, but ${failed} consultant CRM assignment${failed === 1 ? "" : "s"} failed`,
      );
    }
  };

  // Sync each SP's assignedBoardIds so this board's id is added/removed as
  // dictated by the user's selection. We only PATCH SPs whose assignment for
  // this specific board actually changed, so other admins' edits aren't blown
  // away by a stale list.
  const syncSalesPersonAssignments = async (boardId) => {
    if (!boardId) return;
    const toAdd = selectedSpIds.filter((id) => !initialSpIds.includes(id));
    const toRemove = initialSpIds.filter((id) => !selectedSpIds.includes(id));
    if (toAdd.length === 0 && toRemove.length === 0) return;

    const ops = [];
    for (const spId of toAdd) {
      const sp = salesPersons.find((s) => s._id === spId);
      const existing = (sp?.assignedBoardIds || [])
        .map((b) => (typeof b === "string" ? b : b?._id || b?.id))
        .filter(Boolean);
      const next = Array.from(new Set([...existing, boardId]));
      ops.push(updateSalesPersonBoards(spId, next));
    }
    for (const spId of toRemove) {
      const sp = salesPersons.find((s) => s._id === spId);
      const existing = (sp?.assignedBoardIds || [])
        .map((b) => (typeof b === "string" ? b : b?._id || b?.id))
        .filter(Boolean);
      const next = existing.filter((id) => id !== boardId);
      ops.push(updateSalesPersonBoards(spId, next));
    }

    const results = await Promise.allSettled(ops);
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      toast.error(
        `Board saved, but ${failed} sales person assignment update${
          failed === 1 ? "" : "s"
        } failed`,
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      let savedBoardId = board?._id;
      if (board?._id) {
        await updateKanbanBoard(board._id, payload);
      } else {
        const res = await createKanbanBoard(payload);
        savedBoardId =
          res?.data?._id || res?.data?.id || res?._id || res?.id || null;
      }

      if (savedBoardId) {
        await syncSalesPersonAssignments(savedBoardId);
        await syncConsultantAssignments(savedBoardId);
      }

      toast.success(
        board ? "Board updated successfully" : "Board created successfully",
      );

      onSave();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save board",
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedSummary = useMemo(() => {
    const total = selectedSpIds.length + selectedConsultantIds.length;
    if (total === 0) return "No one (default)";
    if (total === 1) {
      if (selectedSpIds.length === 1) {
        const sp = salesPersons.find((s) => s._id === selectedSpIds[0]);
        return sp?.name || sp?.email || "1 sales person";
      }
      const c = consultantsWithRole.find((c) => c._id === selectedConsultantIds[0]);
      const displayName = c?.name || `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || c?.email || "1 consultant";
      return displayName;
    }
    const parts = [];
    if (selectedSpIds.length > 0) parts.push(`${selectedSpIds.length} sales person${selectedSpIds.length === 1 ? "" : "s"}`);
    if (selectedConsultantIds.length > 0) parts.push(`${selectedConsultantIds.length} consultant${selectedConsultantIds.length === 1 ? "" : "s"}`);
    return parts.join(", ") + " selected";
  }, [selectedSpIds, selectedConsultantIds, salesPersons, consultantsWithRole]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-lg max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {board ? "Edit Board" : "Create New Board"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FiX size={22} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-6 space-y-4 overflow-y-auto"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Board Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Fall 2026 Admissions Pipeline"
              maxLength={100}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.name.length}/100 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Add a description for this board..."
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600 resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.description.length}/500 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Associate with Sales Person(s)
            </label>
            <div ref={spDropdownRef}>
              <button
                type="button"
                onClick={() => setSpDropdownOpen((v) => !v)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md bg-white text-left text-sm text-gray-700 flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-600"
              >
                <span className="flex items-center gap-2 truncate">
                  <FiUser size={14} className="text-gray-400" />
                  {loadingSps ? "Loading..." : selectedSummary}
                </span>
                <span className="text-xs text-gray-400">
                  {spDropdownOpen ? "▲" : "▼"}
                </span>
              </button>

              {/* The dropdown panel is rendered inline (in flow) rather than
                  absolutely positioned. This lets the parent form's
                  overflow-y-auto scroll past the panel so the action buttons
                  remain reachable when the list is long. */}
              {spDropdownOpen && (
                <div className="mt-1 w-full bg-white border border-gray-200 rounded-md shadow-sm max-h-64 overflow-y-auto">
                  {/* Sales Persons section */}
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-b border-gray-100">
                    Sales Persons
                  </div>
                  {salesPersons.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 italic">
                      No sales persons available
                    </div>
                  ) : (
                    <ul className="py-1">
                      {salesPersons.map((sp) => {
                        const isSelected = selectedSpIds.includes(sp._id);
                        return (
                          <li key={sp._id}>
                            <button
                              type="button"
                              onClick={() => toggleSpSelection(sp._id)}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-blue-50 ${
                                isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700"
                              }`}
                            >
                              <span className="truncate">
                                {sp.name || sp.email}
                                {sp.email && sp.name && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    {sp.email}
                                  </span>
                                )}
                              </span>
                              {isSelected && (
                                <FiCheck size={14} className="text-blue-600 flex-shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {/* Consultants with CRM Access section */}
                  <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 border-t border-b border-gray-100">
                    Consultants with CRM Access
                  </div>
                  {loadingSps ? (
                    <div className="px-3 py-2 text-xs text-gray-400 italic">
                      Loading...
                    </div>
                  ) : consultantsWithRole.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-400 italic">
                      No consultants with CRM access
                    </div>
                  ) : (
                    <ul className="py-1">
                      {consultantsWithRole.map((c) => {
                        const isSelected = selectedConsultantIds.includes(c._id);
                        const displayName = c.name || `${c.firstName || ""} ${c.lastName || ""}`.trim() || c.email;
                        return (
                          <li key={c._id}>
                            <button
                              type="button"
                              onClick={() => toggleConsultantSelection(c._id)}
                              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-purple-50 ${
                                isSelected ? "bg-purple-50 text-purple-700" : "text-gray-700"
                              }`}
                            >
                              <span className="truncate">
                                {displayName}
                                {c.email && displayName !== c.email && (
                                  <span className="text-xs text-gray-400 ml-2">
                                    {c.email}
                                  </span>
                                )}
                              </span>
                              {isSelected && (
                                <FiCheck size={14} className="text-purple-600 flex-shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Select one, multiple, or none. Leave empty to keep this board
              unassigned.
            </p>
            {(selectedSpIds.length > 0 || selectedConsultantIds.length > 0) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedSpIds.map((id) => {
                  const sp = salesPersons.find((s) => s._id === id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs"
                    >
                      {sp?.name || sp?.email || id}
                      <button
                        type="button"
                        onClick={() => toggleSpSelection(id)}
                        className="hover:text-blue-900"
                      >
                        <FiX size={11} />
                      </button>
                    </span>
                  );
                })}
                {selectedConsultantIds.map((id) => {
                  const c = consultantsWithRole.find((c) => c._id === id);
                  const displayName = c?.name || `${c?.firstName || ""} ${c?.lastName || ""}`.trim() || c?.email || id;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs"
                    >
                      {displayName}
                      <button
                        type="button"
                        onClick={() => toggleConsultantSelection(id)}
                        className="hover:text-purple-900"
                      >
                        <FiX size={11} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {!board && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-800">
              After creating the board you can add stages (columns) and leads.
              Each board starts empty so you can fully customize the pipeline.
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : board ? "Update Board" : "Create Board"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BoardManagementModal;
